<template>
  <div class="p-2 advanced-table-wrapper">
    <div v-if="isLoading" class="loading">
      <img src="@/assets/images/loading.gif" alt="Loading" />
    </div>
    <div v-else-if="isEmptyTable" class="empty">
      <img src="@/assets/images/bulk-empty@2x.png" alt="Empty" />
      <div class="empty-text">
        <span class="empty-text-title">{{ emptyText.type }}</span>
        <span class="empty-text-description">{{emptyText.description}}</span>
      </div>
    </div>
    <template v-else>
      <div class="table-wrapper">
        <table class="advanced-table">
          <thead>
            <tr v-for="headerGroup in table.getHeaderGroups()" :key="headerGroup.id">
              <th
                v-for="header in headerGroup.headers"
                :key="header.id"
                :style="{ width: `${header.getSize()}px` }"
                :colSpan="header.colSpan"
              >
                <FlexRender :render="header.column.columnDef.header" :props="header.getContext()" />
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in table.getRowModel().rows" :key="row.id" :class="table.options.meta?.getRowClasses(row)">
              <td v-for="cell in row.getVisibleCells()" :key="cell.id">
                <template v-if="cell.column.id === 'failedCount'">
                  <span v-if="cell.getValue()" class="error-column-item">
                    <span>
                      <img
                        width="16px"
                        height="16px"
                        src="@/assets/images/icons/alert-circle.svg"
                        alt="Error Icon"
                      />
                      {{ cell.getValue() }} Hata
                    </span>
                    <button class="report-button"
                      @click="openRejectModal({
                        failCount: cell.getValue(),
                        taskId: cell.row.original.taskId,
                        batchId: cell.row.original.taskBatchId,
                      })">
                      <img src="@/assets/images/icons/arrow-right.svg">
                    </button>
                  </span>
                  <span v-else>-</span>
                </template>
                <template v-else-if="cell.column.columnDef.header === 'İşlem'">
                  <AppButton
                    v-if="cell.getValue() === 'FINISHED_SUCCESS' && cell.row.original.downloadable"
                    style="max-width: fit-content; height: 24px !important;"
                    secondary
                    @click="exportJobDetail(cell.row.original.id)"
                    xsm
                    title="İndir"
                  >
                    <template #prependIcon>
                      <Icons name="Download" :class="['download-icon-table']" />
                    </template>
                  </AppButton>
                  <span v-else>-</span>
                </template>
                <template v-else-if="cell.column.id === 'taskSourceFile'">
                  <span v-if="cell.column.id === 'taskSourceFile'">
                    <a
                      class="file-link"
                      target="_blank"
                      :class="{'downloadable': cell.row.original.downloadable}"
                      @click.prevent="handleFileClick(cell.row.original.taskBatchId)"
                    >
                      <span class="icon">
                        <Icons name="Download" :class="['download-icon', {'icon-active': cell.row.original.downloadable }]" />
                      </span>
                      {{ cell.getValue() }}
                    </a>
                  </span>
                </template>
                <template v-else>
                  <FlexRender :render="cell.column.columnDef.cell" :props="cell.getContext()" />
                </template>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div class="pagination flex items-center gap-2">
        <Pagination
          :pageCount="table.getPageCount()"
          :clickHandler="clickHandler"
          :value="table.getState().pagination.pageIndex"
          :pageRange="5"
          firstLastButton="true"
          containerClass="button-group"
          prevLinkClass="border left rounded p-1"
          pageLinkClass="border rounded p-1"
          nextLinkClass="border right rounded p-1"
          :marginPages="0"
        />
      </div>
    </template>
    <RejectReasonModal ref="rejectReasonModalRef" />
  </div>
</template>

<script setup>
import { ref, watch, onMounted, onBeforeUnmount, computed } from 'vue';
import { AppButton, Icons } from '@n11/so-ui-components';
import { FlexRender, getCoreRowModel, useVueTable } from '@tanstack/vue-table';
import { useStore } from 'vuex';
import Pagination from '@/components/Table/Pagination';
import {
  HISTORY_TAB_DOWNLOAD,
  HISTORY_TAB_UPLOAD,
  HEADER_TAB_UPLOAD
} from "@/utils/enum/bulkActionTabs";
import {FILE_DOWNLOAD} from "@/utils/enum/endpoints";
import useService from '../services/getBulkHistoryService';
import { getUploadHistoryColumns, getDownloadHistoryColumns } from '../services/getBulkHistoryColumns';
import RejectReasonModal from './RejectReasonModal.vue';

const INITIAL_PAGE_INDEX = 0;
const INITIAL_PAGE_SIZE = 10;
const rejectReasonModalRef = ref(null);

const store = useStore();
const props = defineProps({
  refreshRef: {
    refresh: Boolean,
    tab: String | null,
    taskId: Number | null,
    headerTab: String
  },
  selectedTabIndex: {
    type: Number,
    required: true,
  },
  selectedHeaderTabIndex: {
    type: String,
    required: true
  }
});

const highlightedRows = ref([]);
const isEmptyTable = ref();
const pagination = ref({
  pageIndex: INITIAL_PAGE_INDEX,
  pageSize: INITIAL_PAGE_SIZE,
  type: props.selectedTabIndex,
  tab: props.selectedHeaderTabIndex,
  reload: false,
});
const emit = defineEmits(['tableStatus']);
const tableColumns = ref(getUploadHistoryColumns());
const { data, pageCount, isLoading } = useService(pagination);

const emptyText = computed(() => {
  switch (props.selectedTabIndex) {
    case HISTORY_TAB_UPLOAD:
      return {
        type: 'Henüz dosya yüklemediniz.',
        description: 'Dosya yüklediğinizde bu ekranda görebilirsiniz.'
      };
    case HISTORY_TAB_DOWNLOAD:
      return {
        type: 'Henüz şablon indirmediniz.',
        description: 'Şablon indirdiğinizde bu ekranda görebilirsiniz.'
      };
  }
});

watch(isLoading, (newVal) => {
  emit('tableStatus', { isLoading: newVal });
});

const table = ref(
  useVueTable({
    get data() {
      return data?.value ?? [];
    },
    get pageCount() {
      return pageCount.value;
    },
    get columns() {
      return tableColumns.value;
    },
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    getRowId: row => row.id,
    onPaginationChange: (updater) => {
      if (typeof updater === 'function') {
        const updated = updater(pagination.value);
        setPagination(updated);
      } else {
        setPagination(updater);
      }
    },
    get rowCount() {
      return data?.value?.length ?? 0;
    },
    state: {
      get pagination() {
        return pagination.value;
      }
    },
    meta: {
      getRowClasses: (row) => highlightedRows.value.includes(row.original.taskId) ? 'success' : '',
    }
  })
);

const updateColumns = () => {
  if (props.selectedHeaderTabIndex === HEADER_TAB_UPLOAD && props.selectedTabIndex === HISTORY_TAB_DOWNLOAD) {
    tableColumns.value = getUploadHistoryColumns(props.selectedHeaderTabIndex);
  } else {
    switch (props.selectedTabIndex) {
      case HISTORY_TAB_DOWNLOAD:
        tableColumns.value = getDownloadHistoryColumns();
        break;
      case HISTORY_TAB_UPLOAD:
        tableColumns.value = getUploadHistoryColumns(props.selectedHeaderTabIndex);
        break;
    }
  }
};

const setPagination = ({ pageIndex, pageSize }) => {
  pagination.value = {
    ...pagination.value,
    pageIndex,
    pageSize,
  };
};

watch(
  () => ({ refresh: props.refreshRef }),
  ({ refresh }) => {
    pagination.value = {
      ...pagination.value,
      pageIndex: INITIAL_PAGE_INDEX,
      type: refresh.tab,
      reload: refresh.refresh,
      tab: refresh.headerTab
    };

    if (refresh.taskId && !highlightedRows.value.includes(refresh.taskId)) {
      highlightedRows.value.push(refresh.taskId);
    }

    updateColumns();
  }
);

watch(
  () => data.value,
  (newValue) => {
    if (newValue) {
      isEmptyTable.value = !newValue.length > 0;
    }
  },
  { immediate: true }
);
const clickHandler = (pageNum) => {
  table.value.setPageIndex(pageNum);
};

const exportJobDetail = async (batchId) => {
  try {
    const { data } = await store.dispatch('products/getExportProductJobDetail', batchId);
    const { fileContent, fileName } = data;

    const binaryString = window.atob(fileContent);
    const bytes = new Uint8Array(binaryString.length);

    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }

    const blob = new Blob([bytes], { type: 'application/zip' });

    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');

    Object.assign(link, {
      href: url,
      download: `${fileName}.zip`,
      style: 'display: none;',
    });

    document.body.appendChild(link);
    link.click();

    document.body.removeChild(link);
    URL.revokeObjectURL(url);

  } catch (error) {
    console.log('Dosya indirme sırasında hata:', error);
  }
};

const handleFileClick = (batchId) => {

  const link = document.createElement('a');
  const url = FILE_DOWNLOAD.replace('{id}', batchId);

  Object.assign(link, {
    href: url,
    style: 'display: none;',
  });

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};
let refreshInterval = null;

const startAutoRefresh = () => {
  refreshInterval = setInterval(() => {
    if (document.hasFocus()) {
      console.log('Refreshing data...');
      pagination.value = {
        ...pagination.value
      };
    }
  }, 60000);
};

const stopAutoRefresh = () => {
  if (refreshInterval) {
    clearInterval(refreshInterval);
    refreshInterval = null;
  }
};

const openRejectModal = ({failCount, taskId, batchId}) => {
  rejectReasonModalRef.value?.openModal(failCount, taskId, batchId);
}

onMounted(() => {
  startAutoRefresh();
  const urlParams = new URLSearchParams(window.location.search);
  const excelReport = urlParams.get('excelReport');
  if (excelReport) {
    tableColumns.value = getDownloadHistoryColumns();
  }
});

onBeforeUnmount(() => {
  stopAutoRefresh();
});
</script>
<style lang="scss" scoped>
@import '@/assets/styles/variables.scss';
@import '@/assets/styles/utilities.scss';
.advanced-table-wrapper {
  display: flex;
  gap: 16px;
  flex-direction: column;
	margin-top: 16px;

  .loading {
    min-height: 647px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .empty {
    min-height: 647px;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 1px solid $colorD40;
    border-radius: 8px;
    flex-direction: column;
    gap: 24px;

    img {
      width: 120px;
      height: 120px;
    }
    &-text {
      font-size: 16px;
      color: $colorN80;
      display: flex;
      align-items: center;
      gap: 12px;
      flex-direction: column;
      font-weight: 500;
      &-title {
        font-weight: 700;
      }
    }
  }

  .table-wrapper {
    width: 100%;
    position: relative;
    border: 1px solid #d1d1d1;
    border-radius: 8px;
    border-bottom: 0px;
    border-spacing: 0;
    border-collapse: separate;
  }

  .advanced-table {
    width: 100%;
    border-radius: 8px;
    border-collapse: separate;

    thead {
      background: $colorD10;
      tr {
        th {
          padding: 12px;
          text-align: left;
          font-weight: 600;
          color: #333;
					font-size: 13px;
        }
				th:not(:last-child) {
					border-right: 1px solid #D3D6DF;
				}
      }
    }

    tbody {
      tr {
        td {
          font-weight: 600;
          padding: 9px 12px;
          text-align: left;
          border-bottom: 2px solid $colorD10;
					font-size: 13px;

					.file-link {
						display: flex;
						align-items: center;
						gap: 8px;
            pointer-events: none;
            color: $colorM50;

            &.downloadable {
              cursor: pointer;
              pointer-events: visible;
              color: $colorM50 !important;
            }

            .download-icon.icon-active {
              color: $colorM50 !important;
            }

					}
        }
				td:not(:last-child) {
					border-right: 1px solid #D3D6DF;
				}

        &.success {
          background-color: $colorS05;
        }
      }
    }
  }
  .download-icon {
    color: $colorM50 !important;
  }

  .download-icon-table {
    color: $colorN80 !important;
  }

  .report-button {
    display: flex;
    padding: 0;
  }
	.error-column-item {
		display: flex;
		flex-direction: column;
		font-size: 12px;
		a {
			color: $colorM50;
			display: flex;
			align-items: center;

			img {
				width: 16px;
				height: 16px;
			}
		}
		span {
			color: $colorE60;
			display: flex;
			align-items: center;
			gap: 4px;
		}
	}
  .status {
    padding: 5px 12px;
    border-radius: 4px;
		font-size: 11px;
		font-weight: 600;
		display: flex;
		align-items: center;
		gap: 4px;
		width: max-content;
		border: 0.5px solid $colorD30;
		img {
			width: 16px;
			height: 16px;
		}
    &.success {
      background-color: $colorS05;
    	color: $colorS50;
    }
    &.failed {
      background-color: $colorE05;
      color: $colorE60;
    }
    &.pending {
      background-color: $colorY05;
    }
  }

  .pagination {
    display: flex;
    justify-content: flex-end;
    margin-top: 16px;
  }
}
</style>
